/**
 * ProConFormModal — modal dialog for creating + editing pros/cons. This is
 * the refactor of `components/ProConForm.tsx`; the workspace owns one
 * instance and reuses it for both panels and both site columns.
 *
 * Linear: MYD-223 (H1).
 */

import { useState, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Button } from 'components/ui/button'
import { Select } from 'components/ui/select'
import { createProCon, updateProCon, fetchProsConsComparison } from 'slices/proConSlice'

const TYPES = ['PRO', 'CON']
const IMPACT_LEVELS = ['LOW', 'MEDIUM', 'HIGH']

interface ProConFormModalProps {
	open: boolean
	onClose: () => void
	evalId: string
	siteId: string
	item?: any
	defaultType?: 'PRO' | 'CON'
}

const ProConFormModal = ({
	open,
	onClose,
	evalId,
	siteId,
	item,
	defaultType,
}: ProConFormModalProps) => {
	const dispatch = useDispatch()
	const isEdit = Boolean(item)

	const [type, setType] = useState<'PRO' | 'CON'>('PRO')
	const [category, setCategory] = useState('')
	const [description, setDescription] = useState('')
	const [impactLevel, setImpactLevel] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		if (open) {
			setType(item?.type || defaultType || 'PRO')
			setCategory(item?.category || '')
			setDescription(item?.description || '')
			setImpactLevel(item?.impactLevel || '')
			setSubmitting(false)
			setError(null)
		}
	}, [open, item, defaultType])

	const handleSubmit = useCallback(async () => {
		if (!category.trim() || !description.trim()) {
			setError('Category and description are required')
			return
		}
		setError(null)
		setSubmitting(true)
		const payload = {
			type,
			category: category.trim(),
			description: description.trim(),
			impactLevel: impactLevel || undefined,
		}
		try {
			if (isEdit) {
				await (dispatch as any)(updateProCon({ evalId, siteId, itemId: item._id, payload }))
			} else {
				await (dispatch as any)(createProCon({ evalId, siteId, payload }))
			}
			// Refresh cross-site comparison so counts stay in sync.
			;(dispatch as any)(fetchProsConsComparison(evalId))
			onClose()
		} finally {
			setSubmitting(false)
		}
	}, [dispatch, evalId, siteId, item, isEdit, type, category, description, impactLevel, onClose])

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<DialogTitle>{isEdit ? 'Edit Pro/Con' : 'Add Pro/Con'}</DialogTitle>
					<DialogDescription>
						Capture a pro or con for this site to inform the comparison.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 pt-2">
					<div className="grid grid-cols-12 gap-3">
						<div className="col-span-4">
							<Label htmlFor="pcType" className="mb-1.5 block">
								Type *
							</Label>
							<Select id="pcType" value={type} onChange={(e: any) => setType(e.target.value)}>
								{TYPES.map((t) => (
									<option key={t} value={t}>
										{t}
									</option>
								))}
							</Select>
						</div>
						<div className="col-span-8">
							<Label htmlFor="pcCat" className="mb-1.5 block">
								Category *
							</Label>
							<Input
								id="pcCat"
								value={category}
								onChange={(e) => setCategory(e.target.value)}
								placeholder="e.g. Location, Traffic, Cost"
							/>
						</div>
					</div>

					<div>
						<Label htmlFor="pcDesc" className="mb-1.5 block">
							Description *
						</Label>
						<Input
							id="pcDesc"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
						/>
					</div>

					<div>
						<Label htmlFor="pcImpact" className="mb-1.5 block">
							Impact Level
						</Label>
						<Select
							id="pcImpact"
							value={impactLevel}
							onChange={(e: any) => setImpactLevel(e.target.value)}
						>
							<option value="">— Not set —</option>
							{IMPACT_LEVELS.map((l) => (
								<option key={l} value={l}>
									{l}
								</option>
							))}
						</Select>
					</div>

					{error && <p className="text-xs text-red-500">{error}</p>}

					<div className="flex justify-end gap-2 pt-2">
						<Button variant="ghost" onClick={onClose}>
							Cancel
						</Button>
						<Button variant="primary" onClick={handleSubmit} disabled={submitting}>
							{submitting ? 'Saving...' : isEdit ? 'Update' : 'Add'}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default ProConFormModal
